/**
 * AuthManager - Unified auth that works with localStorage (guest) or Supabase (cloud)
 */
import { ENV } from '../config/environment';
const STORAGE_KEYS = {
    USER: 'dominauts_user',
    MATCHES: 'dominauts_matches',
    PREFERENCES: 'dominauts_prefs',
};
export class AuthManager {
    static instance;
    currentUser = null;
    supabaseAvailable = false;
    storageAvailable = false;
    constructor() {
        this.supabaseAvailable = !!(ENV.SUPABASE.URL && ENV.SUPABASE.ANON_KEY);
        this.storageAvailable = this.checkStorage();
        this.restoreSession();
    }
    checkStorage() {
        try {
            const key = '__dominauts_test__';
            localStorage.setItem(key, '1');
            localStorage.removeItem(key);
            return true;
        }
        catch {
            return false;
        }
    }
    safeSetItem(key, value) {
        if (!this.storageAvailable)
            return;
        try {
            localStorage.setItem(key, value);
        }
        catch { /* ignore */ }
    }
    static getInstance() {
        if (!AuthManager.instance) {
            AuthManager.instance = new AuthManager();
        }
        return AuthManager.instance;
    }
    restoreSession() {
        if (!this.storageAvailable)
            return;
        try {
            const stored = localStorage.getItem(STORAGE_KEYS.USER);
            if (stored) {
                this.currentUser = JSON.parse(stored);
            }
        }
        catch {
            // Corrupt data, start fresh
            try {
                localStorage.removeItem(STORAGE_KEYS.USER);
            }
            catch { /* ignore */ }
        }
    }
    getUser() {
        return this.currentUser;
    }
    isLoggedIn() {
        return this.currentUser !== null;
    }
    async loginAsGuest(username) {
        const user = {
            id: 'guest_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8),
            username: username || 'Player',
            isGuest: true,
            createdAt: new Date().toISOString(),
        };
        this.currentUser = user;
        this.safeSetItem(STORAGE_KEYS.USER, JSON.stringify(user));
        return user;
    }
    async register(email, password, username) {
        if (this.supabaseAvailable) {
            try {
                const { createClient } = await import('@supabase/supabase-js');
                const supabase = createClient(ENV.SUPABASE.URL, ENV.SUPABASE.ANON_KEY);
                const { data, error } = await supabase.auth.signUp({
                    email,
                    password,
                    options: { data: { username } },
                });
                if (error)
                    throw new Error(error.message);
                if (data.user) {
                    const user = {
                        id: data.user.id,
                        email,
                        username,
                        isGuest: false,
                        createdAt: new Date().toISOString(),
                    };
                    this.currentUser = user;
                    this.safeSetItem(STORAGE_KEYS.USER, JSON.stringify(user));
                    return user;
                }
            }
            catch (e) {
                throw new Error(e.message || 'Registration failed');
            }
        }
        // Fallback: local registration
        const user = {
            id: 'local_' + Date.now(),
            email,
            username,
            isGuest: false,
            createdAt: new Date().toISOString(),
        };
        this.currentUser = user;
        this.safeSetItem(STORAGE_KEYS.USER, JSON.stringify(user));
        return user;
    }
    async login(email, password) {
        if (this.supabaseAvailable) {
            try {
                const { createClient } = await import('@supabase/supabase-js');
                const supabase = createClient(ENV.SUPABASE.URL, ENV.SUPABASE.ANON_KEY);
                const { data, error } = await supabase.auth.signInWithPassword({ email, password });
                if (error)
                    throw new Error(error.message);
                if (data.user) {
                    const user = {
                        id: data.user.id,
                        email,
                        username: data.user.user_metadata?.username || email.split('@')[0],
                        isGuest: false,
                        createdAt: data.user.created_at || new Date().toISOString(),
                    };
                    this.currentUser = user;
                    this.safeSetItem(STORAGE_KEYS.USER, JSON.stringify(user));
                    return user;
                }
            }
            catch (e) {
                throw new Error(e.message || 'Login failed');
            }
        }
        throw new Error('Cloud auth not configured. Use guest mode.');
    }
    async logout() {
        if (this.supabaseAvailable && this.currentUser && !this.currentUser.isGuest) {
            try {
                const { createClient } = await import('@supabase/supabase-js');
                const supabase = createClient(ENV.SUPABASE.URL, ENV.SUPABASE.ANON_KEY);
                await supabase.auth.signOut();
            }
            catch {
                // Continue with local logout
            }
        }
        this.currentUser = null;
        try {
            localStorage.removeItem(STORAGE_KEYS.USER);
        }
        catch { /* ignore */ }
    }
    // Match History
    recordMatch(match) {
        if (!this.storageAvailable)
            return;
        const record = {
            ...match,
            id: 'match_' + Date.now(),
            userId: this.currentUser?.id || 'anonymous',
            timestamp: new Date().toISOString(),
        };
        try {
            const existing = JSON.parse(localStorage.getItem(STORAGE_KEYS.MATCHES) || '[]');
            existing.unshift(record);
            // Keep last 100 matches
            if (existing.length > 100)
                existing.length = 100;
            localStorage.setItem(STORAGE_KEYS.MATCHES, JSON.stringify(existing));
        }
        catch {
            // Storage full or corrupt - try clearing old data
            try {
                localStorage.setItem(STORAGE_KEYS.MATCHES, JSON.stringify([record]));
            }
            catch { /* quota exceeded, skip */ }
        }
    }
    getMatchHistory() {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEYS.MATCHES) || '[]');
        }
        catch {
            return [];
        }
    }
    // Preferences
    savePreference(key, value) {
        if (!this.storageAvailable)
            return;
        try {
            const prefs = JSON.parse(localStorage.getItem(STORAGE_KEYS.PREFERENCES) || '{}');
            prefs[key] = value;
            localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(prefs));
        }
        catch { /* ignore */ }
    }
    getPreference(key, defaultValue) {
        try {
            const prefs = JSON.parse(localStorage.getItem(STORAGE_KEYS.PREFERENCES) || '{}');
            return key in prefs ? prefs[key] : defaultValue;
        }
        catch {
            return defaultValue;
        }
    }
}
